import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, ArrowLeftRight, Boxes, Package, SlidersHorizontal } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Label } from '@/components/ui/Label';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { MetricCard } from '@/components/financials/MetricCard';
import { StockMovementDialog } from '@/components/inventory/StockMovementDialog';
import { AdjustmentDialog } from '@/components/inventory/AdjustmentDialog';
import {
  LOCATION_KIND_LABELS,
  ON_ORDER_PRODUCTION_STATUSES,
  useInventoryLevels,
  useItems,
  type InventoryLevelRow,
  type LocationKind,
} from '@/features/inventory/hooks';
import {
  effectiveUnitCost,
  useItemCostSummaries,
  usePurchaseOrders,
  type PoStatus,
} from '@/features/procurement/hooks';
import { useProductionSchedules } from '@/features/manufacturing/schedules';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatCurrency, formatDate, formatDateOnly, localIsoDate, todayIsoDate } from '@/lib/formatters';

/** POs placed with the vendor whose open lines count as on-order. */
const PLACED_PO_STATUSES = new Set<PoStatus>(['ordered', 'in_production', 'shipped', 'partially_received']);

interface OpenPoLine {
  poNumber: string;
  quantity: number;
  /** Carrier ETA when tracked, otherwise the PO's expected date. */
  arrivalDate: string | null;
  isDraft: boolean;
}

/** Open purchase-order and production-order supply for one item, across every location. */
interface ItemOnOrder {
  placed: number;
  /** Remaining quantity on internal POs not yet placed with the vendor. */
  draft: number;
  /** Earliest arrival among placed lines, falling back to drafts. */
  nextArrival: string | null;
  nextArrivalIsDraft: boolean;
  lines: OpenPoLine[];
}

function OnOrderBreakdown({ onOrder, children }: { onOrder: ItemOnOrder; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent align="end">
        <div className="mb-1 text-[11px] text-muted-foreground">
          Open POs and production orders · all locations
        </div>
        <div className="space-y-0.5">
          {onOrder.lines.map((l, i) => (
            <div key={i} className="flex items-center justify-between gap-4 tabular-nums">
              <span className="font-mono">
                {l.poNumber}
                {l.isDraft && <span className="ml-1 font-sans text-muted-foreground">(draft)</span>}
              </span>
              <span>
                {l.quantity.toLocaleString()} · {l.arrivalDate ? formatDateOnly(l.arrivalDate) : 'No date'}
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function kindLabel(kind: string | null | undefined): string {
  if (!kind) return '';
  return LOCATION_KIND_LABELS[kind as LocationKind] ?? kind;
}

/** Aggregate on-hand per item across every location + lot. */
interface ItemOnHand {
  itemId: string;
  sku: string;
  name: string;
  baseUom: string;
  onHand: number;
  reorderPoint: number | null;
  reorderQty: number | null;
}

/**
 * A row in the levels grid: either a real item × location × lot bucket,
 * or a zero-quantity placeholder for an item that has never had a stock
 * movement and so has no bucket yet.
 */
type LevelGridRow = Pick<
  InventoryLevelRow,
  'id' | 'item_id' | 'quantity' | 'locations' | 'items' | 'lots'
> & {
  updated_at: string | null;
};

/**
 * On-hand inventory — the live rollup of item × location × lot quantities
 * maintained by the stock-movement trigger. Summary tiles up top, a
 * below-reorder-point alert list, then the full levels grid, where items
 * with no stock yet list at zero. Admins can record a movement or post
 * an adjustment straight from here.
 */
export function InventoryLevelsPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { data: levels, isLoading } = useInventoryLevels();
  const { data: items } = useItems();
  const { data: costSummaries } = useItemCostSummaries();
  const { data: purchaseOrders } = usePurchaseOrders();
  const { data: productionOrders } = useProductionSchedules();

  const [movementOpen, setMovementOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [onlyBelowReorder, setOnlyBelowReorder] = useState(false);

  const canManage = canWrite(role, 'inventory');
  const rows = useMemo<InventoryLevelRow[]>(() => levels ?? [], [levels]);
  const unitCost = useCallback(
    (r: LevelGridRow) => effectiveUnitCost(costSummaries?.get(r.item_id), r.items?.std_cost_cents),
    [costSummaries],
  );

  // Per-item on-hand totals, seeded from the item master so SKUs with a
  // reorder point but zero stock still surface as alerts.
  const onHandByItem = useMemo(() => {
    const map = new Map<string, ItemOnHand>();
    const archivedIds = new Set(
      (items ?? []).filter((it) => it.status === 'archived').map((it) => it.id),
    );
    for (const it of items ?? []) {
      if (archivedIds.has(it.id)) continue;
      map.set(it.id, {
        itemId: it.id,
        sku: it.sku,
        name: it.name,
        baseUom: it.base_uom,
        onHand: 0,
        reorderPoint: it.reorder_point,
        reorderQty: it.reorder_qty,
      });
    }
    for (const lvl of rows) {
      if (archivedIds.has(lvl.item_id)) continue;
      const existing = map.get(lvl.item_id);
      if (existing) {
        existing.onHand += lvl.quantity ?? 0;
      } else if (lvl.items) {
        // Fallback for a level whose item isn't in the (possibly still
        // loading) master list yet.
        map.set(lvl.item_id, {
          itemId: lvl.item_id,
          sku: lvl.items.sku,
          name: lvl.items.name,
          baseUom: lvl.items.base_uom,
          onHand: lvl.quantity ?? 0,
          reorderPoint: lvl.items.reorder_point,
          reorderQty: lvl.items.reorder_qty,
        });
      }
    }
    return map;
  }, [items, rows]);

  const onOrderByItem = useMemo(() => {
    const map = new Map<string, ItemOnOrder>();
    const add = (itemId: string, line: OpenPoLine) => {
      let entry = map.get(itemId);
      if (!entry) {
        entry = { placed: 0, draft: 0, nextArrival: null, nextArrivalIsDraft: false, lines: [] };
        map.set(itemId, entry);
      }
      if (line.isDraft) entry.draft += line.quantity;
      else entry.placed += line.quantity;
      entry.lines.push(line);
    };
    for (const po of purchaseOrders ?? []) {
      const isDraft = po.status === 'internal_po';
      if (!isDraft && !PLACED_PO_STATUSES.has(po.status)) continue;
      const arrivalDate = (po.tracking_estimated_delivery ?? po.expected_at)?.slice(0, 10) ?? null;
      for (const line of po.po_lines ?? []) {
        if (!line.item_id) continue;
        const remaining = Math.round(
          (Number(line.quantity) - Number(line.received_quantity ?? 0)) * (Number(line.conversion_to_base) || 1),
        );
        if (remaining <= 0) continue;
        add(line.item_id, { poNumber: po.po_number, quantity: remaining, arrivalDate, isDraft });
      }
    }
    for (const order of productionOrders ?? []) {
      if (!order.item_id) continue;
      const isDraft = order.status === 'draft';
      if (!isDraft && !ON_ORDER_PRODUCTION_STATUSES.includes(order.status)) continue;
      const remaining = order.quantity - (order.actual_quantity ?? 0);
      if (remaining <= 0) continue;
      add(order.item_id, {
        poNumber: order.work_center ? `Production · ${order.work_center.code}` : 'Production',
        quantity: remaining,
        arrivalDate: localIsoDate(order.planned_end),
        isDraft,
      });
    }
    for (const entry of map.values()) {
      entry.lines.sort(
        (a, b) =>
          Number(a.isDraft) - Number(b.isDraft) ||
          (a.arrivalDate ?? '9999').localeCompare(b.arrivalDate ?? '9999'),
      );
      const next = entry.lines.find((l) => l.arrivalDate);
      entry.nextArrival = next?.arrivalDate ?? null;
      entry.nextArrivalIsDraft = next?.isDraft ?? false;
    }
    return map;
  }, [purchaseOrders, productionOrders]);
  const today = todayIsoDate();

  const belowReorder = useMemo(
    () =>
      Array.from(onHandByItem.values())
        .filter((i) => i.reorderPoint != null && i.onHand < i.reorderPoint)
        .sort((a, b) => a.onHand - b.onHand),
    [onHandByItem],
  );
  const belowReorderIds = useMemo(
    () => new Set(belowReorder.map((i) => i.itemId)),
    [belowReorder],
  );

  const stats = useMemo(() => {
    const distinctItems = new Set(rows.filter((r) => (r.quantity ?? 0) !== 0).map((r) => r.item_id));
    const totalUnits = rows.reduce((acc, r) => acc + (r.quantity ?? 0), 0);
    const totalValueCents = rows.reduce(
      (acc, r) => acc + (r.quantity ?? 0) * (unitCost(r) ?? 0),
      0,
    );
    return {
      distinctItems: distinctItems.size,
      totalUnits,
      totalValueCents,
    };
  }, [rows, unitCost]);

  const gridRows = useMemo<LevelGridRow[]>(() => {
    const stockedIds = new Set(rows.map((r) => r.item_id));
    const zeroRows: LevelGridRow[] = (items ?? [])
      .filter((it) => it.status !== 'archived' && !stockedIds.has(it.id))
      .map((it) => ({
        id: `no-stock:${it.id}`,
        item_id: it.id,
        quantity: 0,
        updated_at: null,
        locations: null,
        lots: null,
        items: {
          sku: it.sku,
          name: it.name,
          base_uom: it.base_uom,
          reorder_point: it.reorder_point,
          reorder_qty: it.reorder_qty,
          std_cost_cents: it.std_cost_cents,
        },
      }));
    const all: LevelGridRow[] = [...rows, ...zeroRows];
    return onlyBelowReorder ? all.filter((r) => belowReorderIds.has(r.item_id)) : all;
  }, [items, rows, onlyBelowReorder, belowReorderIds]);

  const columns = useMemo<ColumnDef<LevelGridRow>[]>(
    () => [
      {
        id: 'sku',
        accessorFn: (r) => r.items?.sku ?? '',
        header: 'SKU',
        size: 150,
        minSize: 90,
        cell: ({ row }) => (
          <Link
            to={`/catalog/items/${row.original.item_id}?tab=inventory`}
            onClick={(e) => e.stopPropagation()}
            className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
          >
            {row.original.items?.sku ?? '—'}
          </Link>
        ),
      },
      {
        id: 'name',
        accessorFn: (r) => r.items?.name ?? '',
        header: 'Item',
        size: 240,
        minSize: 140,
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-medium text-foreground">{row.original.items?.name ?? '—'}</span>
            {belowReorderIds.has(row.original.item_id) && (
              <Badge variant="warning">Low</Badge>
            )}
          </span>
        ),
      },
      {
        id: 'location',
        accessorFn: (r) => r.locations?.name ?? '',
        header: 'Location',
        size: 180,
        minSize: 120,
        cell: ({ row }) => (
          <span>
            {row.original.locations?.name ?? '—'}
            {row.original.locations?.code && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({row.original.locations.code})
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'kind',
        accessorFn: (r) => r.locations?.kind ?? '',
        header: 'Location kind',
        size: 150,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) =>
          row.original.locations?.kind ? (
            <Badge variant="secondary">{kindLabel(row.original.locations.kind)}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'lot',
        accessorFn: (r) => r.lots?.lot_number ?? '',
        header: 'Lot',
        size: 130,
        minSize: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.lots?.lot_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => <span className="block text-right">On-hand</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {Number(row.original.quantity).toLocaleString()}
            <span className="ml-1 text-xs text-muted-foreground">
              {row.original.items?.base_uom}
            </span>
          </span>
        ),
      },
      {
        id: 'on_order',
        accessorFn: (r) => onOrderByItem.get(r.item_id)?.placed ?? 0,
        header: () => <span className="block text-right">On-order</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const onOrder = onOrderByItem.get(row.original.item_id);
          if (!onOrder) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <OnOrderBreakdown onOrder={onOrder}>
              <span className="block text-right tabular-nums">
                {onOrder.placed > 0 ? (
                  <>
                    {onOrder.placed.toLocaleString()}
                    <span className="ml-1 text-xs text-muted-foreground">{row.original.items?.base_uom}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
                {onOrder.draft > 0 && (
                  <span className="block text-[11px] text-muted-foreground">
                    +{onOrder.draft.toLocaleString()} draft
                  </span>
                )}
              </span>
            </OnOrderBreakdown>
          );
        },
      },
      {
        id: 'arrival',
        accessorFn: (r) => onOrderByItem.get(r.item_id)?.nextArrival ?? '',
        header: 'Arrival date',
        size: 130,
        minSize: 100,
        cell: ({ row }) => {
          const onOrder = onOrderByItem.get(row.original.item_id);
          if (!onOrder) return <span className="text-muted-foreground">—</span>;
          const { nextArrival, nextArrivalIsDraft, lines } = onOrder;
          const otherDates = new Set(lines.map((l) => l.arrivalDate ?? '')).size - 1;
          const late = !!nextArrival && !nextArrivalIsDraft && nextArrival < today;
          return (
            <OnOrderBreakdown onOrder={onOrder}>
              <span className="block whitespace-nowrap">
                {nextArrival ? (
                  <span className={late ? 'text-fresh-salmon-dark' : undefined}>{formatDateOnly(nextArrival)}</span>
                ) : (
                  <span className="text-muted-foreground">No date</span>
                )}
                {(late || nextArrivalIsDraft || otherDates > 0) && (
                  <span className="block text-[11px] text-muted-foreground">
                    {[late && 'Overdue', nextArrivalIsDraft && 'Draft', otherDates > 0 && `+${otherDates} more`]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </span>
            </OnOrderBreakdown>
          );
        },
      },
      {
        id: 'value',
        accessorFn: (r) => (r.quantity ?? 0) * (unitCost(r) ?? 0),
        header: () => <span className="block text-right">Value</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const cost = unitCost(row.original);
          if (cost == null) return <span className="block text-right text-muted-foreground">—</span>;
          return (
            <span className="block text-right tabular-nums text-muted-foreground">
              {formatCurrency((row.original.quantity ?? 0) * cost)}
            </span>
          );
        },
      },
      {
        id: 'updated_at',
        accessorKey: 'updated_at',
        header: 'Updated',
        size: 110,
        minSize: 100,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
    ],
    [belowReorderIds, unitCost, onOrderByItem, today],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'location', title: 'Location' },
      { columnId: 'kind', title: 'Location kind' },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="On-hand inventory"
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setAdjustmentOpen(true)}>
                <SlidersHorizontal className="h-4 w-4" />
                Adjust
              </Button>
              <Button onClick={() => setMovementOpen(true)}>
                <ArrowLeftRight className="h-4 w-4" />
                Record movement
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="SKUs in stock"
          value={stats.distinctItems.toLocaleString()}
          loading={isLoading}
          icon={<Package className="h-4 w-4 text-muted-foreground" />}
        />
        <MetricCard
          label="Total units on-hand"
          value={stats.totalUnits.toLocaleString()}
          loading={isLoading}
          icon={<Boxes className="h-4 w-4 text-muted-foreground" />}
        />
        <MetricCard
          label="Inventory value"
          value={formatCurrency(stats.totalValueCents)}
          loading={isLoading}
        />
        <MetricCard
          label="Below reorder point"
          value={belowReorder.length.toLocaleString()}
          loading={isLoading}
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      {belowReorder.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-fresh-salmon-dark" />
              Reorder alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {belowReorder.slice(0, 12).map((i) => (
                <Link
                  key={i.itemId}
                  to={`/catalog/items/${i.itemId}?tab=inventory`}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2 transition-colors hover:bg-secondary/60"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {i.name}
                    </span>
                    <span className="block truncate font-mono text-[11px] text-muted-foreground">
                      {i.sku}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block tabular-nums text-sm text-fresh-salmon-dark">
                      {i.onHand.toLocaleString()} / {i.reorderPoint?.toLocaleString()}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">on-hand / reorder</span>
                  </span>
                </Link>
              ))}
            </div>
            {belowReorder.length > 12 && (
              <button
                type="button"
                onClick={() => setOnlyBelowReorder(true)}
                className="mt-3 text-xs text-muted-foreground hover:text-foreground"
              >
                + {belowReorder.length - 12} more — show only low-stock rows below
              </button>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable<LevelGridRow, unknown>
            data={gridRows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`/catalog/items/${r.item_id}?tab=inventory`)}
            searchPlaceholder="Search by SKU, item, location, or lot…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200]}
            tableId="inventory-levels"
            toolbarSlot={
              <div className="flex items-center gap-2">
                <Switch
                  id="below-reorder"
                  checked={onlyBelowReorder}
                  onCheckedChange={setOnlyBelowReorder}
                />
                <Label htmlFor="below-reorder" className="cursor-pointer text-xs text-muted-foreground">
                  Only below reorder
                </Label>
              </div>
            }
            mobileCard={{
              title: (row) => row.items?.name ?? '—',
              subtitle: (row) => <span className="font-mono text-[11px]">{row.items?.sku}</span>,
              badges: (row) => [
                row.locations?.kind ? (
                  <Badge key="kind" variant="secondary">
                    {kindLabel(row.locations.kind)}
                  </Badge>
                ) : null,
                belowReorderIds.has(row.item_id) ? (
                  <Badge key="low" variant="warning">
                    Low
                  </Badge>
                ) : null,
              ],
              amount: (row) => ({
                value: `${Number(row.quantity).toLocaleString()} ${row.items?.base_uom ?? ''}`,
                caption: row.locations?.name ?? undefined,
                tone: 'neutral',
              }),
              meta: (row) => {
                const m: { label: string; value: React.ReactNode }[] = [];
                if (row.lots?.lot_number) m.push({ label: 'Lot', value: row.lots.lot_number });
                const onOrder = onOrderByItem.get(row.item_id);
                if (onOrder && onOrder.placed > 0) {
                  m.push({ label: 'On-order', value: onOrder.placed.toLocaleString() });
                }
                if (onOrder?.nextArrival) {
                  m.push({ label: 'Arrival', value: formatDateOnly(onOrder.nextArrival) });
                }
                const cost = unitCost(row);
                if (cost != null) {
                  m.push({ label: 'Value', value: formatCurrency((row.quantity ?? 0) * cost) });
                }
                return m;
              },
            }}
            emptyState={
              <EmptyState
                icon={Boxes}
                title={onlyBelowReorder ? 'Nothing below reorder' : 'No inventory yet'}
                action={
                  canManage && !onlyBelowReorder ? (
                    <Button onClick={() => setMovementOpen(true)}>
                      <ArrowLeftRight className="h-4 w-4" />
                      Record movement
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {canManage && (
        <>
          <StockMovementDialog open={movementOpen} onOpenChange={setMovementOpen} />
          <AdjustmentDialog open={adjustmentOpen} onOpenChange={setAdjustmentOpen} />
        </>
      )}
    </div>
  );
}
